import React, { memo, useMemo, useState } from 'react';
import { getWindowTheme } from '../config/windowTheme';
import { BlogPost } from '../data/blog';
import { formatBlogText } from '../utils/formatBlogText';

interface BlogDetailWindowProps {
  post: BlogPost;
  isDarkMode?: boolean;
}

function buildGallery(coverImage: string, photos: string[]): string[] {
  const gallery: string[] = [];
  if (coverImage) gallery.push(coverImage);
  for (const src of photos) {
    if (src && !gallery.includes(src)) gallery.push(src);
  }
  return gallery;
}

const BlogDetailWindow = memo<BlogDetailWindowProps>(({ post, isDarkMode = false }) => {
  const dark = isDarkMode;
  const t = getWindowTheme(dark);
  const [activePhoto, setActivePhoto] = useState(0);

  const gallery = useMemo(
    () => buildGallery(post.coverImage, post.photos),
    [post.coverImage, post.photos],
  );

  const formattedDate = new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const bodyText = dark ? 'text-white/70' : t.body;
  const ledeText = dark ? 'text-white/55' : t.body;
  const divider = dark ? 'border-white/10' : t.border;

  return (
    <div
      className={`min-h-full ${t.bg} pb-10`}
      style={{ animation: 'fadeIn 0.25s ease-out' }}
    >
      <div className={`sticky top-0 z-30 border-b ${t.border} ${t.headerBg}`}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-2.5 sm:py-3">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border ${t.pillActive}`}>
              {post.category}
            </span>
            <time dateTime={post.date} className={t.meta}>
              {formattedDate}
            </time>
            {gallery.length > 1 && (
              <span className={`${t.meta} ml-auto`}>
                {gallery.length} photos
              </span>
            )}
          </div>
          <h1 className={`font-black text-base sm:text-lg md:text-xl leading-tight tracking-tight line-clamp-2 ${t.head}`}>
            {post.title}
          </h1>
        </div>
      </div>

      <article className="max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {post.excerpt && (
          <p className={`mb-5 sm:mb-6 text-sm sm:text-base leading-relaxed border-l-2 border-pink-400/70 pl-3 sm:pl-4 ${ledeText}`}>
            {post.excerpt}
          </p>
        )}

        {/* Photo gallery — cover + photos[] */}
        {gallery.length > 0 && (
          <figure className="mb-5 sm:mb-7">
            <div className="relative rounded-xl overflow-hidden bg-black/10">
              <img
                src={gallery[activePhoto]}
                alt={gallery.length > 1 ? `${post.title}, ${activePhoto + 1} of ${gallery.length}` : post.title}
                className="w-full max-h-48 sm:max-h-80 object-cover select-none"
                draggable={false}
              />
              {gallery.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setActivePhoto(i => (i - 1 + gallery.length) % gallery.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white text-sm backdrop-blur-sm hover:bg-pink-500/70 transition-colors"
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => setActivePhoto(i => (i + 1) % gallery.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white text-sm backdrop-blur-sm hover:bg-pink-500/70 transition-colors"
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-2 right-2 font-mono text-[9px] text-white/90 bg-black/55 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                    {activePhoto + 1} / {gallery.length}
                  </div>
                </>
              )}
            </div>

            {gallery.length > 1 && (
              <figcaption className="mt-2">
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                  {gallery.map((src, i) => (
                    <button
                      key={src + i}
                      type="button"
                      onClick={() => setActivePhoto(i)}
                      aria-label={`Photo ${i + 1}`}
                      aria-current={i === activePhoto ? 'true' : undefined}
                      className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                        i === activePhoto
                          ? 'border-pink-400 opacity-100 ring-1 ring-pink-400/30'
                          : dark
                            ? 'border-white/10 opacity-55 hover:opacity-85'
                            : 'border-black/10 opacity-65 hover:opacity-100'
                      }`}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                    </button>
                  ))}
                </div>
              </figcaption>
            )}
          </figure>
        )}

        {/* Body */}
        <div className={`border-t pt-5 sm:pt-6 ${divider}`}>
          <div className="space-y-4 sm:space-y-5">
            {post.content.map((paragraph, i) => (
              <p
                key={i}
                className={`text-sm sm:text-[15px] leading-[1.75] sm:leading-[1.8] indent-6 sm:indent-8 text-justify ${bodyText} ${
                  i === 0 ? 'text-[15px] sm:text-base' : ''
                }`}
              >
                {formatBlogText(paragraph, dark)}
              </p>
            ))}
          </div>
        </div>

        <footer className={`mt-8 pt-4 border-t ${divider}`}>
          <p className={t.meta}>
            {post.category} · {formattedDate}
          </p>
        </footer>
      </article>

      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
});

BlogDetailWindow.displayName = 'BlogDetailWindow';

export default BlogDetailWindow;
